// frontend/src/pages/admin/AdminVehicles.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { vehiclesApi } from '../../api/vehicles';
import { storesApi } from '../../api/stores';
import { zonesApi } from '../../api/zones';
import { aiApi } from '../../api/ai';
import uploadService from '../../services/upload';
import { compressImage } from '../../utils/imageCompression';
import helpers from '../../utils/helpers';
import CameraModal from '../../components/common/CameraModal';
import { 
    FaSearch, FaFilter, FaCheck, FaTimes, FaEye, FaTrash, FaUndo, 
    FaPlus, FaCar, FaSpinner, FaEdit,
    FaCamera, FaFolderOpen, FaStore, FaMagic, FaArrowLeft, FaMapMarkerAlt, FaMoneyBillWave,
    FaClipboardCheck, FaShieldAlt, FaExclamationTriangle, FaTags, FaCalculator, FaRocket
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// --- CONFIGURATION CONSTANT ---
const EXCLUSIVE_STORE_ID = "everything_automotive_exclusive";
// ------------------------------

const REJECTION_REASONS = [
  { id: 'blurry_photos', label: 'Images are blurry or low quality.' },
  { id: 'inaccurate_description', label: 'Description is inaccurate or misleading.' },
  { id: 'price_unrealistic', label: 'Price is unrealistic (too high or low).' },
  { id: 'duplicate_listing', label: 'This is a duplicate listing.' },
  { id: 'contact_info_in_listing', label: 'Contact information found in photos or description.' },
  { id: 'incomplete_listing', label: 'Listing is incomplete (missing key details).'},
];

const initialFormState = {
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    discount_percentage: 0, // <--- NEW FIELD
    condition: 'nigerian_used',
    transmission: 'automatic',
    mileage: '',
    vin: '', 
    color: '',
    location_state: '',
    location_lga_id: '', 
    location_lga: '',    
    location_city: '',   
    zone_id: '',         
    description: '',
    features: '',
    store_id: EXCLUSIVE_STORE_ID, 
    pay_on_delivery_status: 'Available', 
    refundable_status: 'Not Specified',
    expected_delivery_date: ''
};

const INITIAL_IMAGES = {
    front: null,
    back: null,
    side: null,
    interior: null,
    engine: null,
    boot: null,
    dashboard: null,
    front_seats: null,
    center_console: null,
    tire: null,
    vin_label: null,
    top: null
};

function AdminVehicles() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [listings, setListings] = useState([]);
  const [stores, setStores] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [zones, setZones] = useState([]);
  const [filteredZones, setFilteredZones] = useState([]);

  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const [isRejectionModalOpen, setIsRejectionModal] = useState(false);
  const [rejectionTarget, setRejectionTarget] = useState(null); 
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [otherReasonText, setOtherReasonText] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null); 
  const [formData, setFormData] = useState(initialFormState);
  const [imageUrls, setImageUrls] = useState(INITIAL_IMAGES);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [activeViewType, setActiveViewType] = useState(null);

  const fetchListings = useCallback(async () => {
    try {
      const [listingsData, storesData, lgasData, zonesData] = await Promise.all([
        vehiclesApi.getAllListingsAdmin(),
        storesApi.getAllStores(),
        zonesApi.getAllLgas(), 
        zonesApi.getAllZones() 
      ]);
      setListings(listingsData);
      setStores(storesData);
      setLgas(lgasData);
      setZones(zonesData);
    } catch (err) {
      alert("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  useEffect(() => {
    let result = listings;
    if (filterStatus !== 'all') {
      result = result.filter(l => l.status === filterStatus);
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(l => 
        `${l.year} ${l.make} ${l.model}`.toLowerCase().includes(lower) ||
        (l.seller_email && l.seller_email.toLowerCase().includes(lower))
      );
    }
    setFilteredListings(result);
    setSelectedIds([]);
  }, [listings, filterStatus, searchTerm]);

  const groupedLgas = useMemo(() => {
    return lgas.reduce((acc, lga) => {
      if (!lga.state || !lga.lga) return acc;
      const stateKey = lga.state.trim();
      if (!acc[stateKey]) acc[stateKey] = [];
      acc[stateKey].push(lga);
      return acc;
    }, {});
  }, [lgas]);

  const availableLgas = formData.location_state ? groupedLgas[formData.location_state] || [] : [];

  useEffect(() => {
      if (formData.location_lga_id) {
          const relevantZones = zones.filter(z => z.lga_id === formData.location_lga_id && z.is_active);
          setFilteredZones(relevantZones);
      } else {
          setFilteredZones([]);
      }
  }, [formData.location_lga_id, zones]);

  const handleSelectAll = (e) => {
      if (e.target.checked) {
          const allIds = filteredListings.map(l => l.id);
          setSelectedIds(allIds);
      } else {
          setSelectedIds([]);
      }
  };

  const handleSelectOne = (id) => {
      setSelectedIds(prev => {
          if (prev.includes(id)) {
              return prev.filter(item => item !== id);
          } else {
              return [...prev, id];
          }
      });
  };

  const handleBulkDelete = async () => {
      if (selectedIds.length === 0) return;
      if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} vehicles?`)) return;

      setIsBulkDeleting(true);
      try {
          await Promise.all(selectedIds.map(id => vehiclesApi.deleteListingAdmin(id)));
          alert("Selected vehicles deleted successfully.");
          setSelectedIds([]);
          fetchListings();
      } catch (err) {
          alert("Some items could not be deleted.");
          fetchListings();
      } finally {
          setIsBulkDeleting(false);
      }
  };

  const handleGenerateDescription = async () => {
    if (!formData.make || !formData.model || !formData.condition) {
        alert("Please fill in Make, Model, and Condition first.");
        return;
    }
    setIsGenerating(true);
    try {
        const selectedStore = stores.find(s => s.id === formData.store_id);
        const storeName = selectedStore ? selectedStore.store_name : null;
        const payload = {
            item_type: 'vehicle',
            name: `${formData.year} ${formData.make} ${formData.model}`,
            make: formData.make,
            model: formData.model,
            condition: formData.condition,
            features: formData.features,
            store_name: storeName
        };
        const response = await aiApi.generateDescription(payload);
        if (response.description) {
            setFormData(prev => ({ ...prev, description: response.description }));
        } else {
            alert("AI failed to generate a description.");
        }
    } catch (err) {
        alert("AI Generation failed.");
    } finally {
        setIsGenerating(false);
    }
  };

  const openRejectionModal = (listing) => {
    setRejectionTarget(listing);
    setIsRejectionModal(true);
  };

  const closeRejectionModal = () => {
    setIsRejectionModal(false);
    setRejectionTarget(null);
    setSelectedReasons([]);
    setOtherReasonText('');
  };

  const handleReasonToggle = (reasonId) => {
    setSelectedReasons(prev => 
      prev.includes(reasonId) ? prev.filter(r => r !== reasonId) : [...prev, reasonId]
    );
  };

  const handleConfirmRejection = async () => {
    if (!rejectionTarget) return;
    let finalReason = REJECTION_REASONS.filter(r => selectedReasons.includes(r.id)).map(r => r.label).join(', ');
    if (selectedReasons.includes('other') && otherReasonText) {
      finalReason += (finalReason ? ', ' : '') + otherReasonText;
    }
    if (!finalReason) {
        alert("Please select at least one reason.");
        return;
    }
    setUpdatingId(rejectionTarget.id);
    try {
      await vehiclesApi.updateListingStatusAdmin(rejectionTarget.id, 'rejected', finalReason);
      setListings(prev => prev.map(l => l.id === rejectionTarget.id ? { ...l, status: 'rejected' } : l));
      closeRejectionModal();
    } catch (err) {
      alert("Failed to reject listing.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm(`Are you sure you want to APPROVE this listing?`)) return;
    setUpdatingId(id);
    try {
      await vehiclesApi.updateListingStatusAdmin(id, 'active');
      setListings(prev => prev.map(l => l.id === id ? { ...l, status: 'active' } : l));
    } catch (err) {
      alert("Failed to approve listing.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("PERMANENT ACTION: Are you sure you want to delete this listing?")) {
        setUpdatingId(id);
        try {
            await vehiclesApi.deleteListingAdmin(id);
            setListings(prev => prev.filter(l => l.id !== id));
            setSelectedIds(prev => prev.filter(itemId => itemId !== id));
        } catch (err) {
            alert("Failed to delete listing.");
        } finally {
            setUpdatingId(null);
        }
    }
  };

  const handleOpenModal = (vehicle = null) => {
      if (vehicle) {
          setEditingVehicle(vehicle);
          let foundLgaId = '';
          if (vehicle.location_lga && vehicle.location_state) {
              const stateLgas = groupedLgas[vehicle.location_state] || [];
              const found = stateLgas.find(l => l.lga === vehicle.location_lga);
              if (found) foundLgaId = found.id;
          }
          setFormData({
              make: vehicle.make,
              model: vehicle.model,
              year: vehicle.year,
              price: vehicle.price,
              discount_percentage: vehicle.discount_percentage || 0, // <--- LOAD DISCOUNT
              condition: vehicle.condition,
              transmission: vehicle.transmission,
              mileage: vehicle.mileage,
              vin: vehicle.vin || '',
              color: vehicle.color,
              location_state: vehicle.location_state,
              location_lga: vehicle.location_lga,
              location_lga_id: foundLgaId,
              location_city: vehicle.location_city || '',
              zone_id: vehicle.zone_id || '',
              description: vehicle.description || '',
              features: vehicle.features ? vehicle.features.join(', ') : '',
              store_id: vehicle.store_id || EXCLUSIVE_STORE_ID,
              pay_on_delivery_status: vehicle.pay_on_delivery_status || 'Available',
              refundable_status: vehicle.refundable_status || 'Not Specified',
              expected_delivery_date: vehicle.expected_delivery_date || ''
          });
          setImageUrls({ ...INITIAL_IMAGES, ...vehicle.images });
      } else {
          setEditingVehicle(null);
          setFormData(initialFormState);
          setImageUrls(INITIAL_IMAGES);
      }
      setIsModalOpen(true);
  };

  const handleCloseModal = () => {
      setIsModalOpen(false);
      setEditingVehicle(null);
      setFormData(initialFormState);
      setImageUrls(INITIAL_IMAGES);
  };

  const handleInputChange = (e) => {
      const { name, value } = e.target;
      if (name === 'location_state') {
          setFormData(prev => ({ ...prev, location_state: value, location_lga_id: '', location_lga: '', zone_id: '' }));
      } else if (name === 'location_lga_id') {
          const selectedLga = lgas.find(l => l.id === value);
          setFormData(prev => ({ ...prev, location_lga_id: value, location_lga: selectedLga ? selectedLga.lga : '', zone_id: '' }));
      } else {
          setFormData(prev => ({ ...prev, [name]: value }));
      }
  };

  const handleCameraClick = (viewType) => { setActiveViewType(viewType); setIsCameraOpen(true); };
  const handleCameraCapture = async (file) => { await processFile(file, activeViewType); setIsCameraOpen(false); };
  const processFile = async (file, viewType) => {
      if (!file) return;
      setUploadingSlot(viewType);
      try {
          const compressed = await compressImage(file, 0.5);
          const folderPath = `vehicle-sale-images/${user.id}/${Date.now()}`;
          const response = await uploadService.uploadImage(compressed, folderPath);
          setImageUrls(prev => ({ ...prev, [viewType]: response.url }));
      } catch (err) { alert(`Failed to upload ${viewType}.`); } finally { setUploadingSlot(null); }
  };
  const handleFileInput = (e, viewType) => { processFile(e.target.files[0], viewType); };

  const handleSubmit = async (e) => {
      e.preventDefault();
      if (!imageUrls.front) return alert("Front View image is required.");
      setSubmitting(true);
      try {
          const payload = {
              ...formData,
              year: parseInt(formData.year),
              price: parseFloat(formData.price),
              discount_percentage: parseFloat(formData.discount_percentage) || 0, // <--- SEND DISCOUNT
              mileage: parseInt(formData.mileage),
              features: formData.features ? formData.features.split(',').map(f => f.trim()).filter(f => f !== '') : [],
              images: Object.fromEntries(Object.entries(imageUrls).filter(([_, v]) => v != null)),
              store_id: formData.store_id || null
          };
          delete payload.location_lga_id;
          if (editingVehicle) {
              await vehiclesApi.updateListing(editingVehicle.id, payload);
              alert("Vehicle updated successfully!");
          } else {
              const res = await vehiclesApi.createListing(payload);
              if (res.vehicle && res.vehicle.id) { await vehiclesApi.updateListingStatusAdmin(res.vehicle.id, 'active'); }
              alert("Vehicle created and activated successfully!");
          }
          handleCloseModal();
          fetchListings();
      } catch (err) { alert(`Error: ${err.message}`); } finally { setSubmitting(false); }
  };

  const getStatusBadge = (status) => {
    const styles = { pending_review: "bg-yellow-100 text-yellow-800", active: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-800", sold: "bg-gray-100 text-gray-800" };
    return <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${styles[status] || styles.sold}`}>{status.replace('_', ' ')}</span>;
  };

  const getStoreName = (id) => {
      if (id === EXCLUSIVE_STORE_ID) return "Everything Automotive Exclusive Store"; 
      const store = stores.find(s => s.id === id);
      return store ? store.store_name : 'Private Seller';
  };

  const getInspectionBadge = (status) => {
      switch(status) {
          case 'certified': return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded font-bold flex items-center gap-1"><FaShieldAlt /> Certified</span>;
          case 'inspected': return <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded font-bold flex items-center gap-1"><FaClipboardCheck /> Inspected</span>;
          case 'failed': return <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded font-bold flex items-center gap-1"><FaExclamationTriangle /> Failed</span>;
          default: return <span className="bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded">Not Verified</span>;
      }
  };

  // Helper to calculate final price for display in modal
  const calculateFinalPrice = () => {
      const price = parseFloat(formData.price) || 0;
      const discount = parseFloat(formData.discount_percentage) || 0;
      if (discount <= 0) return price;
      return price - (price * (discount / 100));
  };

  if (loading) return <div className="p-8 text-center">Loading listings...</div>;

  return (
    <div>
      <div className="mb-4">
          <button onClick={() => navigate('/admin/ea-hub')} className="text-gray-500 hover:text-primary flex items-center gap-2 text-sm font-bold"><FaArrowLeft /> Back to EA Hub</button>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2"><FaCar className="text-primary" /> Vehicle Marketplace</h1>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button onClick={() => handleOpenModal()} className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded flex items-center justify-center gap-2 w-full sm:w-auto"><FaPlus /> Add Vehicle</button>
          </div>
      </div>


      {/* FILTERS & BULK ACTIONS */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2 w-full md:w-auto">
          <FaFilter className="text-gray-400" />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-gray-300 rounded px-3 py-2">
            <option value="all">All Listings</option>
            <option value="pending_review">Pending Review</option>
            <option value="active">Active</option>
            <option value="rejected">Rejected</option>
            <option value="sold">Sold</option>
          </select>
        </div>
        <div className="relative w-full md:w-64">
          <input type="text" placeholder="Search Make, Model, Email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded"/>
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
        {selectedIds.length > 0 && (
            <button onClick={handleBulkDelete} disabled={isBulkDeleting} className="bg-red-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2 hover:bg-red-700 transition-colors shadow-sm w-full md:w-auto justify-center">
                {isBulkDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />} Delete Selected ({selectedIds.length})
            </button>
        )}
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider border-b">
                <th className="p-4 w-10"><input type="checkbox" onChange={handleSelectAll} checked={filteredListings.length > 0 && selectedIds.length === filteredListings.length} className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer" /></th>
                <th className="p-4">Listing</th>
                <th className="p-4">Store</th>
                <th className="p-4">Seller</th>
                <th className="p-4">Price</th>
                <th className="p-4">Status</th>
                <th className="p-4">Inspection</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredListings.map((listing) => (
                <tr key={listing.id} className={`hover:bg-gray-50 ${selectedIds.includes(listing.id) ? 'bg-blue-50' : ''}`}>
                  <td className="p-4"><input type="checkbox" checked={selectedIds.includes(listing.id)} onChange={() => handleSelectOne(listing.id)} className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer" /></td>
                  <td className="p-4">
                      <div className="font-bold text-gray-800">{listing.year} {listing.make} {listing.model}</div>
                      <div className="text-xs text-gray-500">{listing.condition.replace('_', ' ')}</div>
                      {listing.discount_percentage > 0 && (
                          <span className="text-[10px] bg-red-100 text-red-600 px-1 rounded font-bold">-{listing.discount_percentage}% OFF</span>
                      )}
                  </td>
                  <td className="p-4 text-sm text-gray-600"><span className={`px-2 py-1 rounded text-xs ${listing.store_id === EXCLUSIVE_STORE_ID ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>{getStoreName(listing.store_id)}</span></td>
                  <td className="p-4 text-sm"><div className="font-medium text-gray-800">{listing.seller_name}</div><div className="text-xs text-gray-500">{listing.seller_email}</div></td>
                  <td className="p-4 text-sm font-bold text-primary">{helpers.formatCurrency(listing.price, 'NGN')}</td>
                  <td className="p-4">{getStatusBadge(listing.status)}</td>
                  <td className="p-4">{getInspectionBadge(listing.inspection_status)}</td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/vehicles/${listing.id}`} target="_blank" className="p-2 text-gray-600 hover:bg-gray-200 rounded" title="View Public Page"><FaEye /></Link>
                      <Link to={`/admin/inspections/new/${listing.id}`} className="p-2 text-purple-600 hover:bg-purple-100 rounded" title="Perform Inspection"><FaClipboardCheck /></Link>
                      <button onClick={() => handleOpenModal(listing)} className="p-2 text-blue-600 hover:bg-blue-100 rounded" title="Edit Listing"><FaEdit /></button>
                      {listing.status === 'pending_review' && (
                        <>
                          <button onClick={() => handleApprove(listing.id)} disabled={updatingId === listing.id} className="p-2 text-green-600 hover:bg-green-100 rounded" title="Approve"><FaCheck /></button>
                          <button onClick={() => openRejectionModal(listing)} disabled={updatingId === listing.id} className="p-2 text-red-600 hover:bg-red-100 rounded" title="Reject"><FaTimes /></button>
                        </>
                      )}
                      {listing.status === 'active' && (
                        <button onClick={() => openRejectionModal(listing)} disabled={updatingId === listing.id} className="p-2 text-yellow-600 hover:bg-yellow-100 rounded" title="Reject/Unlist"><FaUndo /></button>
                      )}
                      <button onClick={() => handleDelete(listing.id)} disabled={updatingId === listing.id} className="p-2 text-red-600 hover:bg-red-100 rounded" title="Delete Permanently"><FaTrash /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- REJECTION MODAL --- */}
      {isRejectionModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg animate-fadeInUp">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold">Reason for Rejection</h3>
              <p className="text-sm text-gray-500">Select one or more reasons. This will be saved for your records.</p>
            </div>
            <div className="p-6 space-y-3 max-h-64 overflow-y-auto">
              {REJECTION_REASONS.map(reason => (
                <label key={reason.id} className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={selectedReasons.includes(reason.id)} onChange={() => handleReasonToggle(reason.id)} className="h-5 w-5 text-primary rounded focus:ring-primary" />
                  <span className="ml-3 text-sm text-gray-700">{reason.label}</span>
                </label>
              ))}
              <label className="flex items-center p-3 border rounded cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={selectedReasons.includes('other')} onChange={() => handleReasonToggle('other')} className="h-5 w-5 text-primary rounded focus:ring-primary" />
                <span className="ml-3 text-sm text-gray-700">Other (Please specify)</span>
              </label>
              {selectedReasons.includes('other') && (
                <textarea 
                  value={otherReasonText}
                  onChange={(e) => setOtherReasonText(e.target.value)}
                  placeholder="Enter custom reason here..."
                  className="w-full p-2 border rounded mt-2 h-20"
                />
              )}
            </div>
            <div className="p-6 bg-gray-50 flex justify-end gap-3">
              <button onClick={closeRejectionModal} className="px-4 py-2 text-gray-700">Cancel</button>
              <button onClick={handleConfirmRejection} disabled={updatingId} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                {updatingId ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- ADD/EDIT VEHICLE MODAL --- */}
      {isModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-4">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-fadeInUp">
                  <div className="flex justify-between items-center p-6 border-b">
                      <h3 className="text-xl font-bold">{editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}</h3>
                      <button onClick={handleCloseModal}><FaTimes /></button>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4">
                      
                      {/* Store Assignment */}
                      <div className="bg-blue-50 p-4 rounded border border-blue-100 mb-4">
                          <label className="block text-sm font-bold text-blue-800 mb-1 flex items-center gap-2">
                              <FaStore /> Assign to Store *
                          </label>
                          <select 
                              name="store_id" 
                              value={formData.store_id} 
                              onChange={handleInputChange} 
                              required
                              className="p-2 border rounded w-full bg-white focus:ring-2 focus:ring-blue-500"
                          >
                              <option value="">-- Select Store --</option>
                              <optgroup label="Internal / EA Branches">
                                  <option value={EXCLUSIVE_STORE_ID}>Everything Automotive (System Default)</option>
                                  {stores.filter(s => s.id !== EXCLUSIVE_STORE_ID && s.is_internal).map(store => (
                                      <option key={store.id} value={store.id}>{store.store_name}</option>
                                  ))}
                              </optgroup>
                              <optgroup label="Partner Stores">
                                  {stores.filter(s => !s.is_internal).map(store => (
                                      <option key={store.id} value={store.id}>{store.store_name}</option>
                                  ))}
                              </optgroup>
                          </select>
                      </div>

                      {/* Images */}
                      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                          {Object.keys(imageUrls).map(key => (
                              <div key={key} className="relative border-2 border-dashed rounded h-24 flex flex-col items-center justify-center text-center p-1 hover:bg-gray-50 transition-colors">
                                  {uploadingSlot === key ? <FaSpinner className="animate-spin text-primary" /> : (
                                      imageUrls[key] ? <img src={imageUrls[key]} alt={key} className="w-full h-full object-cover rounded" /> : (
                                          <div className="flex flex-col items-center gap-1">
                                              <button type="button" onClick={() => handleCameraClick(key)} className="text-blue-600 hover:text-blue-800"><FaCamera size={16} /></button>
                                              <span className="text-xs text-gray-400">OR</span>
                                              <label className="cursor-pointer text-gray-500 hover:text-primary">
                                                  <FaFolderOpen size={16} />
                                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileInput(e, key)} />
                                              </label>
                                          </div>
                                      )
                                  )}
                                  <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-[9px] py-1 capitalize truncate px-1">
                                      {key.replace('_', ' ')} {key === 'front' && '*'}
                                  </span>
                                  {imageUrls[key] && (
                                      <button type="button" onClick={() => setImageUrls(prev => ({ ...prev, [key]: null }))} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 text-[8px]"><FaTimes /></button>
                                  )}
                              </div>
                          ))}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Make *</label><input name="make" value={formData.make} onChange={handleInputChange} required className="w-full p-2 border rounded" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Model *</label><input name="model" value={formData.model} onChange={handleInputChange} required className="w-full p-2 border rounded" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Year *</label><input type="number" name="year" value={formData.year} onChange={handleInputChange} required className="w-full p-2 border rounded" /></div>
                      </div>

                      {/* --- PRICING & DISCOUNT SECTION --- */}
                      <div className="bg-blue-50 p-4 rounded border border-blue-100">
                          <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2"><FaTags /> Pricing & Discounts</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                              <div>
                                  <label className="block text-xs font-bold text-gray-600 mb-1">Original Price (NGN) *</label>
                                  <input type="number" name="price" value={formData.price} onChange={handleInputChange} required className="w-full p-2 border rounded" />
                              </div>
                              <div>
                                  <label className="block text-xs font-bold text-gray-600 mb-1">Discount (%)</label>
                                  <input 
                                      type="number" 
                                      name="discount_percentage" 
                                      value={formData.discount_percentage} 
                                      onChange={handleInputChange} 
                                      min="0" 
                                      max="100"
                                      className="w-full p-2 border rounded" 
                                      placeholder="0"
                                  />
                              </div>
                              <div className="bg-white p-2 rounded border border-blue-200 text-right">
                                  <span className="block text-xs text-gray-500 uppercase font-bold">Final Price</span>
                                  <span className="text-lg font-bold text-green-600 flex items-center justify-end gap-2">
                                      <FaCalculator className="text-gray-400 text-xs" />
                                      {helpers.formatCurrency(calculateFinalPrice(), 'NGN')}
                                  </span>
                              </div>
                          </div>
                      </div>
                      {/* ---------------------------------- */}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Mileage (km)</label><input type="number" name="mileage" value={formData.mileage} onChange={handleInputChange} required className="w-full p-2 border rounded" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Color</label><input type="text" name="color" value={formData.color} onChange={handleInputChange} required className="w-full p-2 border rounded" /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">VIN / Chassis</label><input type="text" name="vin" value={formData.vin} onChange={handleInputChange} placeholder="17-digit VIN" className="w-full p-2 border rounded uppercase" maxLength={17} /></div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">Condition</label>
                              <select name="condition" value={formData.condition} onChange={handleInputChange} className="w-full p-2 border rounded bg-white">
                                  <option value="nigerian_used">Nigerian Used</option>
                                  <option value="foreign_used">Foreign Used (Tokunbo)</option>
                                  <option value="new">Brand New</option>
                              </select>
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">Transmission</label>
                              <select name="transmission" value={formData.transmission} onChange={handleInputChange} className="w-full p-2 border rounded bg-white">
                                  <option value="automatic">Automatic</option>
                                  <option value="manual">Manual</option>
                              </select>
                          </div>
                      </div>

                      {/* --- LOCATION SECTION --- */}
                      <div className="bg-gray-50 p-4 rounded border border-gray-200">
                          <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2"><FaMapMarkerAlt /> Vehicle Location</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                              <div>
                                  <label className="block text-xs font-bold text-gray-600 mb-1">State *</label>
                                  <select name="location_state" value={formData.location_state} onChange={handleInputChange} required className="w-full p-2 border rounded bg-white">
                                      <option value="">Select State</option>
                                      {Object.keys(groupedLgas).sort().map(state => (
                                          <option key={state} value={state}>{state}</option>
                                      ))}
                                  </select>
                              </div>
                              <div>
                                  <label className="block text-xs font-bold text-gray-600 mb-1">LGA *</label>
                                  <select name="location_lga_id" value={formData.location_lga_id} onChange={handleInputChange} required disabled={!formData.location_state} className="w-full p-2 border rounded bg-white disabled:bg-gray-100">
                                      <option value="">Select LGA</option>
                                      {availableLgas.map(lga => (
                                          <option key={lga.id} value={lga.id}>{lga.lga}</option>
                                      ))}
                                  </select>
                              </div>
                              <div>
                                  <label className="block text-xs font-bold text-gray-600 mb-1">Zone (Optional)</label>
                                  <select name="zone_id" value={formData.zone_id} onChange={handleInputChange} disabled={!formData.location_lga_id} className="w-full p-2 border rounded bg-white disabled:bg-gray-100">
                                      <option value="">Select Zone</option>
                                      {filteredZones.map(z => (
                                          <option key={z.id} value={z.id}>{z.zone_name}</option>
                                      ))}
                                  </select>
                              </div>
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">City / Area (Optional)</label>
                              <input name="location_city" value={formData.location_city} onChange={handleInputChange} placeholder="e.g. Allen Avenue" className="w-full p-2 border rounded" />
                          </div>
                      </div>

                      {/* --- PURCHASE POLICIES --- */}
                      <div className="bg-blue-50 p-4 rounded border border-blue-100">
                          <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2"><FaMoneyBillWave /> Purchase Policies</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                  <label className="block text-xs font-bold text-gray-600 mb-1">Pay on Delivery</label>
                                  <select name="pay_on_delivery_status" value={formData.pay_on_delivery_status} onChange={handleInputChange} className="w-full p-2 border rounded bg-white"><option value="Available">Available</option><option value="Not Available">Not Available</option></select>
                              </div>
                              <div>
                                  <label className="block text-xs font-bold text-gray-600 mb-1">Refund Policy</label>
                                  <select name="refundable_status" value={formData.refundable_status} onChange={handleInputChange} className="w-full p-2 border rounded bg-white"><option value="Not Specified">Not Specified</option><option value="Refundable">Refundable</option><option value="Non-Refundable">Non-Refundable</option></select>
                              </div>
                          </div>
                      </div>

                      <div className="mb-6">
                          <label className="block text-xs font-bold text-gray-600 mb-1">Features (Comma separated)</label>
                          <input type="text" name="features" value={formData.features} onChange={handleInputChange} placeholder="e.g. Leather Seats, Sunroof, Reverse Camera" className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" />
                      </div>

                      <div className="mb-8">
                          <div className="flex justify-between items-center mb-1">
                              <label className="block text-xs font-bold text-gray-600">Description</label>
                              <button type="button" onClick={handleGenerateDescription} disabled={isGenerating} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-bold flex items-center gap-1 hover:bg-purple-200 disabled:opacity-50">
                                  {isGenerating ? <FaSpinner className="animate-spin" /> : <FaMagic />} AI Generate
                              </button>
                          </div>
                          <textarea name="description" rows="4" value={formData.description} onChange={handleInputChange} className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"></textarea>
                      </div>

                      <div className="flex justify-end gap-3 pt-4 border-t">
                          <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-gray-600">Cancel</button>
                          <button type="submit" disabled={submitting || isGenerating} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark">
                              {submitting ? 'Saving...' : (editingVehicle ? 'Update Listing' : 'Create Listing')}
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* CAMERA MODAL */}
      <CameraModal 
          isOpen={isCameraOpen} 
          onClose={() => setIsCameraOpen(false)} 
          onCapture={handleCameraCapture}
          viewType={activeViewType || 'image'}
      />
    </div>
  );
}

export default AdminVehicles;
